import { Component } from 'react';
import PropTypes from 'prop-types';
import { createRef } from 'react';
import debounce from 'transmute/debounce';
import BaseDialog from 'customer-data-ui-utilities/dialog/BaseDialog';
import PortalIdParser from 'PortalIdParser';
import enviro from 'enviro';
import styled from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
const IFRAME_MARGIN = 60;
const IFRAME_HEADER = 68;
const IFRAME_PADDING = 40;
const MODAL_MIN_WIDTH = 150;
const SEISMIC_APP_IDS = [239477];
const StyledIframe = styled.iframe.withConfig({
  displayName: "ProfileIframeModal__StyledIframe",
  componentId: "sc-12r688h-0"
})(["border:none;"]);
// See https://git.hubteam.com/HubSpot/DeveloperExperienceFrontendPlanning/issues/443

export default class ProfileIframeModal extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      iframe: /*#__PURE__*/createRef(),
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight
    };
    this.handleResize = () => {
      return this.setState({
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight
      });
    };
    this.debouncedHandleResize = debounce(100, this.handleResize);
    this.getIframeWidth = () => {
      const {
        width
      } = this.props;
      const maxWidth = this.state.windowWidth - 2 * IFRAME_MARGIN - 2 * IFRAME_PADDING;
      if (width && width <= maxWidth) {
        return width;
      }
      return maxWidth;
    };
    this.getIframeHeight = () => {
      const {
        height
      } = this.props;
      const maxHeight = this.state.windowHeight - 2 * IFRAME_MARGIN - IFRAME_HEADER - 2 * IFRAME_PADDING;
      if (height && height <= maxHeight) {
        return height;
      }
      return maxHeight;
    };
    this.getModalWidth = () => {
      return Math.max(this.getIframeWidth() + 2 * IFRAME_PADDING, MODAL_MIN_WIDTH);
    };
    this.getUri = () => {
      const {
        uri
      } = this.props;
      return `${uri}#${document.location.origin}`;
    };
    this.getIsSeismicApp = () => {
      // Check if this is the Seismic app, which has an exception to `allow-top-navigation`. This only applies
      // to their app ID, and only on 53 (or QA equivalent).
      // See https://git.hubteam.com/HubSpot/DeveloperExperienceFrontendPlanning/issues/443
      const {
        applicationId
      } = this.props;
      const portalId = PortalIdParser.get();
      return applicationId && SEISMIC_APP_IDS.includes(applicationId) && enviro.isProd() ? portalId === 53 : portalId === 99535353;
    };
    this.handleReject = () => {
      const {
        closeIframeModal,
        onReject
      } = this.props;
      if (typeof onReject === 'function') {
        onReject();
      }
      return closeIframeModal();
    };
    this.handleIframeMessage = event => {
      const {
        closeIframeModal,
        onCancelIframeMessage,
        onDoneIframeMessage
      } = this.props;

      // derived from: https://git.hubteam.com/HubSpot/interframe/blob/fdb00a2385e0160b4b5d279a3fbfa82c1aa2bc50/static/js/index.js#L100-L112
      if (!event || !event.data || !this.state.iframe.current || event.source.location !== this.state.iframe.current.contentWindow.location) {
        // if not meant for this origin, ignore
        return;
      }
      let message;
      if (typeof event.data === 'string') {
        message = JSON.parse(event.data);
      } else if (typeof event.data === 'object') {
        message = event.data;
      }
      if (!message) return;
      switch (message.action) {
        case 'CANCEL':
          if (typeof onCancelIframeMessage === 'function') {
            onCancelIframeMessage();
          }
          closeIframeModal();
          return;
        case 'DONE':
          if (typeof onDoneIframeMessage === 'function') {
            onDoneIframeMessage();
          }
          closeIframeModal();
          return;
        default:
          return;
      }
    };
    this.render = () => {
      const {
        footer,
        title
      } = this.props;
      const src = this.getUri();
      const sandbox = [...(this.props.sandbox || []), 'allow-scripts', 'allow-same-origin', 'allow-forms', 'allow-popups', 'allow-popups-to-escape-sandbox'];
      if (src.startsWith('https://tools.hubteam') || this.getIsSeismicApp()) {
        sandbox.push('allow-top-navigation');
      }
      return /*#__PURE__*/_jsx(BaseDialog, {
        onReject: this.handleReject,
        onConfirm: function () {},
        showCancelButton: false,
        showConfirmButton: false,
        title: title,
        width: this.getModalWidth(),
        dialogClassName: footer ? undefined : 'p-bottom-10',
        includeFooter: footer != null,
        extraFooter: footer,
        children: /*#__PURE__*/_jsx(StyledIframe, {
          "data-test-id": "record-timeline-iframe",
          allow: "camera; microphone",
          src: src
          // @ts-expect-error ts-migrate(2322) FIXME: Type 'RefObject<unknown>' is not assignable to typ... Remove this comment to see the full error message
          ,
          ref: this.state.iframe,
          sandbox: sandbox.join(' '),
          referrerPolicy: "no-referrer-when-downgrade",
          style: {
            height: this.getIframeHeight(),
            width: this.getIframeWidth()
          }
        })
      });
    };
  }
  componentDidMount() {
    window.addEventListener('resize', this.debouncedHandleResize);
    window.addEventListener('message', this.handleIframeMessage);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.debouncedHandleResize);
    window.removeEventListener('message', this.handleIframeMessage);
  }
}
ProfileIframeModal.displayName = 'ProfileIframeModal';
ProfileIframeModal.propTypes = {
  applicationId: PropTypes.number,
  closeIframeModal: PropTypes.func.isRequired,
  footer: PropTypes.node,
  height: PropTypes.number,
  onCancelIframeMessage: PropTypes.func,
  onDoneIframeMessage: PropTypes.func,
  onReject: PropTypes.func,
  sandbox: PropTypes.array,
  title: PropTypes.node,
  uri: PropTypes.string.isRequired,
  width: PropTypes.number
};